import {
  addMonthsClamped,
  contractBalanceOn,
  contractSchedule,
  formatDateLong,
  formatMonthLong,
  monthOf,
  parseAmount,
  type InterestMethod,
  type LoanContract,
  type LoanDto,
  type LoanPatch,
} from '@koverta/shared';
import { useState } from 'react';
import { useToast } from '../components/toast';
import { Btn, Dialog, MoneyInput } from '../components/ui';
import { errorMessage, patch } from '../lib/api';
import { useHousehold } from '../lib/household';
import { fmtPercent, parsePercent } from '../lib/loan';
import { invalidateScopes, useAccounts, usePlan } from '../lib/queries';

/**
 * Edit every term of a loan the way the bank shows it: amount borrowed, payout date, term, nominal and effective
 * rate, installment (anuitet); how it's paid; and what is still owed (loans.md §1, §4).
 */
export function EditLoanDialog({ loan, onClose }: { loan: LoanDto; onClose: () => void }) {
  const { fmt, today } = useHousehold();
  const { toast } = useToast();
  const cur = loan.currency;
  const accounts = (useAccounts().data ?? []).filter(
    (a) => a.onBudget && (!a.closed || a.id === loan.fromAccountId),
  );
  const plan = usePlan(monthOf(today)).data;
  const categories = (plan?.groups ?? []).flatMap((g) =>
    g.categories.filter((c) => !c.hidden).map((c) => ({ id: c.id, name: `${g.name} › ${c.name}` })),
  );
  const firstRate = loan.rates[0]?.annualRate ?? loan.currentRate;

  const [borrowed, setBorrowed] = useState(fmt.num(loan.originalPrincipal, 2));
  const [opened, setOpened] = useState(loan.startDate);
  const [term, setTerm] = useState(loan.termMonths ? String(loan.termMonths) : '');
  const [nominal, setNominal] = useState(fmt.rate(firstRate));
  const [effective, setEffective] = useState(loan.effectiveRate !== null ? fmt.rate(loan.effectiveRate) : '');
  const [installment, setInstallment] = useState(fmt.num(loan.installment, 2));
  const [method, setMethod] = useState<InterestMethod>(loan.interestMethod);
  const [firstPay, setFirstPay] = useState(loan.firstPayment);
  const [next, setNext] = useState(loan.nextPayment ?? '');
  const [nextTouched, setNextTouched] = useState(false);
  const [from, setFrom] = useState(loan.fromAccountId ?? '');
  const [category, setCategory] = useState(loan.categoryId ?? '');
  const [owed, setOwed] = useState(fmt.num(loan.balance, 2));
  const [owedDate, setOwedDate] = useState(today);
  const [owedTouched, setOwedTouched] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const principal = parseAmount(borrowed, fmt.locale);
  const inst = parseAmount(installment, fmt.locale);
  const rate = parsePercent(nominal, fmt);
  const months = term.trim() ? Number(term) : null;
  // The contract's first payment fixes the payment day; the next payment follows it unless set by hand.
  const paymentDay = firstPay ? Number(firstPay.slice(8, 10)) : loan.paymentDay;

  // The bank's plan rebuilt from what is typed, to check the term and suggest what is owed today.
  const contract: LoanContract | null =
    principal && principal > 0 && inst && inst > 0 && rate !== null && opened
      ? {
          originalPrincipal: principal,
          startDate: opened,
          paymentDay,
          installment: inst,
          rates: [{ effectiveFrom: opened, annualRate: rate }, ...loan.rates.slice(1)],
          method,
        }
      : null;
  const check = (() => {
    if (!contract) return null;
    const sched = contractSchedule(contract);
    return {
      sched,
      now: contractBalanceOn(contract, today),
      nextDue: sched.rows.find((r) => r.date > today)?.date ?? null,
    };
  })();
  const nextPayment = nextTouched || !check?.nextDue || firstPay === loan.firstPayment ? next : check.nextDue;

  const save = async () => {
    if (!principal || principal <= 0) return setErr('Enter the amount borrowed');
    if (!inst || inst <= 0) return setErr('Enter the monthly installment');
    if (rate === null) return setErr('Enter the nominal interest rate, e.g. 5,99');
    const eff = effective.trim() ? parsePercent(effective, fmt) : null;
    if (effective.trim() && eff === null)
      return setErr('Enter the effective rate, e.g. 7,56, or leave it empty');
    if (months !== null && !(Number.isInteger(months) && months > 0))
      return setErr('Enter the term as a number of monthly payments, or leave it empty');
    const balance = parseAmount(owed, fmt.locale);
    if (owedTouched && (balance === null || balance < 0)) return setErr('Enter what is still owed');
    if (firstPay && firstPay <= opened) return setErr('The first payment must be after the loan was opened');
    if (nextPayment && nextPayment <= opened)
      return setErr('The next payment must be after the loan was opened');

    const body: LoanPatch = {};
    if (principal !== loan.originalPrincipal) body.originalPrincipal = principal;
    if (opened !== loan.startDate) body.startDate = opened;
    if (months !== loan.termMonths) body.termMonths = months;
    if (rate !== firstRate) body.annualRate = rate;
    if (eff !== loan.effectiveRate) body.effectiveRate = eff;
    if (inst !== loan.installment) body.installment = inst;
    if (method !== loan.interestMethod) body.interestMethod = method;
    if (nextPayment && nextPayment !== loan.nextPayment) body.nextPayment = nextPayment;
    if (paymentDay !== loan.paymentDay) body.paymentDay = paymentDay;
    if (from && from !== loan.fromAccountId) body.fromAccountId = from;
    if (category && category !== loan.categoryId) body.categoryId = category;
    if (owedTouched && balance !== null) {
      body.balance = balance;
      body.balanceDate = owedDate;
    }
    if (!Object.keys(body).length) return onClose();
    setBusy(true);
    setErr(null);
    try {
      await patch(`/api/loans/${loan.accountId}`, body);
      invalidateScopes(['accounts', 'plan', 'transactions', 'activity']);
      toast(`Saved ${loan.name}`);
      onClose();
    } catch (e) {
      setErr(errorMessage(e));
    }
    setBusy(false);
  };

  const planPayments = check?.sched.rows.length ?? 0;
  const termMismatch = check && months && check.sched.payoffDate && planPayments !== months;
  return (
    <Dialog
      title={`Edit loan · ${loan.name}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="sp" />
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="primary" disabled={busy} onClick={save}>
            Save loan
          </Btn>
        </>
      }
    >
      <div className="h4" style={{ margin: 0 }}>
        Contract · as in your bank's app
      </div>
      <div className="fgrid">
        <label className="field">
          Amount borrowed ({cur})
          <MoneyInput
            className="input r"

            value={borrowed}
            onChange={setBorrowed}
          />
          <small className="muted">Iznos kredita</small>
        </label>
        <label className="field">
          Opened on
          <input className="input" type="date" value={opened} onChange={(e) => setOpened(e.target.value)} />
          <small className="muted">The day the money was paid out</small>
        </label>
        <label className="field">
          First payment
          <input
            className="input"
            type="date"
            value={firstPay}
            onChange={(e) => setFirstPay(e.target.value)}
          />
          <small className="muted">Its day of the month is the payment day</small>
        </label>
        <label className="field">
          Nominal interest rate (%)
          <input
            className="input r"
            inputMode="decimal"
            value={nominal}
            onChange={(e) => setNominal(e.target.value)}
            aria-invalid={rate === null}
          />
          <small className="muted">
            Nominalna kamatna stopa; interest is computed from this
            {loan.rates.length > 1 ? '. Later rate changes are kept' : ''}
          </small>
        </label>
        <label className="field">
          Effective interest rate (%)
          <input
            className="input r"
            inputMode="decimal"
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
            placeholder="optional"
          />
          <small className="muted">Efektivna (EKS): includes fees; shown for reference only</small>
        </label>
        <label className="field">
          Monthly installment ({cur})
          <MoneyInput
            className="input r"

            value={installment}
            onChange={setInstallment}
          />
          <small className="muted">Anuitet</small>
        </label>
        <label className="field">
          Term (number of payments)
          <input
            className="input r"
            inputMode="numeric"
            value={term}
            onChange={(e) => setTerm(e.target.value.replace(/\D/g, ''))}
            placeholder="e.g. 60"
          />
          <small className="muted">
            {check?.sched.rows[0] && months
              ? `${formatDateLong(check.sched.rows[0].date, fmt.locale)} – ${formatDateLong(
                  addMonthsClamped(check.sched.rows[0].date, months - 1),
                  fmt.locale,
                )}`
              : 'Leave empty if unknown'}
          </small>
        </label>
        <label className="field">
          Interest
          <select
            className="select"
            value={method}
            onChange={(e) => setMethod(e.target.value as InterestMethod)}
          >
            <option value="30_360">Monthly (30/360), usual for annuity loans</option>
            <option value="actual_365">Daily (actual/365)</option>
          </select>
        </label>
      </div>
      {check ? (
        <div className={`loan-check ${termMismatch ? 'warn' : ''}`} role="status">
          {check.sched.payoffDate ? (
            <>
              By these terms: {planPayments} payments, the last on{' '}
              <b>{formatDateLong(check.sched.payoffDate, fmt.locale)}</b>,{' '}
              {fmt.money(check.sched.totalInterest, cur, 2)} interest in total.
              {termMismatch
                ? ` The term says ${months} payments: check the installment and the rate against the contract.`
                : ''}
            </>
          ) : (
            'At this installment the loan is never paid off: check the installment and the rate.'
          )}
        </div>
      ) : null}

      <div className="h4" style={{ margin: '6px 0 0' }}>
        Payments
      </div>
      <div className="fgrid">
        <label className="field">
          Next payment
          <input
            className="input"
            type="date"
            value={nextPayment}
            onChange={(e) => {
              setNext(e.target.value);
              setNextTouched(true);
            }}
          />
          <small className="muted">
            {!loan.nextPayment ? 'No payment is scheduled. ' : ''}
            {check?.nextDue && check.nextDue !== nextPayment ? (
              <button
                className="link"
                type="button"
                onClick={() => {
                  setNext(check.nextDue!);
                  setNextTouched(true);
                }}
              >
                Use {formatDateLong(check.nextDue, fmt.locale)} from the contract
              </button>
            ) : (
              'Its day of the month becomes the payment day'
            )}
          </small>
        </label>
        <label className="field">
          Pay from
          <select className="select" value={from} onChange={(e) => setFrom(e.target.value)}>
            {!from ? <option value="">Choose an account</option> : null}
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.currency})
              </option>
            ))}
          </select>
        </label>
        <label className="field wide">
          Payment category
          <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
            {!category ? <option value="">Choose a category</option> : null}
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="h4" style={{ margin: '6px 0 0' }}>
        Still owed
      </div>
      <div className="fgrid">
        <label className="field">
          Remaining principal ({cur})
          <MoneyInput
            className="input r"
            value={owed}
            onChange={(t) => {
              setOwed(t);
              setOwedTouched(true);
            }}
          />
        </label>
        <label className="field">
          As of the end of
          <input
            className="input"
            type="date"
            value={owedDate}
            onChange={(e) => {
              setOwedDate(e.target.value);
              setOwedTouched(true);
            }}
          />
        </label>
      </div>
      <div className="muted" style={{ fontSize: 12.5 }}>
        Ovce na broju has {fmt.money(loan.balance, cur, 2)} owed today.
        {check && check.now.after ? (
          <>
            {' '}
            The contract plan says <b>{fmt.money(check.now.balance, cur, 2)}</b> after the payment on{' '}
            {formatDateLong(check.now.after, fmt.locale)}.{' '}
            <button
              className="link"
              type="button"
              onClick={() => {
                // After the last payment so far, so it is what is owed today.
                setOwed(fmt.num(check.now.balance, 2));
                setOwedDate(today);
                setOwedTouched(true);
              }}
            >
              Use this
            </button>
          </>
        ) : null}{' '}
        Enter the remaining principal (preostali dug), not the payoff amount (iznos za likvidaciju): that one
        adds the interest since the last payment and any early-repayment fee. Saving a different figure enters
        a balance adjustment on the loan.
      </div>
      {loan.rates.length > 1 ? (
        <div className="muted" style={{ fontSize: 12.5 }}>
          Rate changes:{' '}
          {loan.rates
            .map(
              (r) => `${fmtPercent(r.annualRate, fmt)} from ${formatDateLong(r.effectiveFrom, fmt.locale)}`,
            )
            .join(' · ')}
          . Paid off by {check?.sched.payoffDate ? formatMonthLong(monthOf(check.sched.payoffDate)) : '—'}.
        </div>
      ) : null}
      {err ? <div className="err-t">{err}</div> : null}
    </Dialog>
  );
}
